import PageHeader from "@/components/PageHeader";
import { HandHeart, Users, Award, Compass, ExternalLink } from "lucide-react";
import CTABand from "@/components/CTABand";

const PROGRAMS = [
  {
    icon: HandHeart,
    title: "Caregiver Support",
    body: "Respite grants, education, and support groups for the family caregivers who hold their loved ones' lives together.",
  },
  {
    icon: Users,
    title: "Community Outreach",
    body: "Health-screening events, transportation assistance, and food/medication delivery in underserved Georgia neighborhoods.",
  },
  {
    icon: Award,
    title: "Workforce Scholarships",
    body: "Tuition and certification grants that open the door to stable healthcare careers for first-generation entrants.",
  },
  {
    icon: Compass,
    title: "Veteran & Disability Advocacy",
    body: "Resource navigation, benefits guidance, and one-to-one support for veterans and individuals with disabilities.",
  },
];

export default function Foundation() {
  return (
    <div data-testid="page-foundation">
      <PageHeader
        overline="Community Partner · 501(c)(3)"
        title="Guardian Grace Family"
        italic="& Community Foundation."
        description="A community partner of 1st Touch Enterprise Holdings — extending the mission beyond the clinic, into the homes, neighborhoods, and lives of the people we serve."
        image="https://images.pexels.com/photos/5206930/pexels-photo-5206930.jpeg"
      />

      <section className="py-24 bg-teh-bg">
        <div className="teh-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="teh-overline">Why It Matters</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 text-teh-deep leading-tight">
              Healthcare can't fix
              <br />
              <span className="italic text-teh-green">what poverty breaks.</span>
            </h2>
            <a
              href="https://guardiangrace.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="foundation-page-external"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-teh-green text-white text-sm font-medium rounded-full hover:bg-teh-greenDark hover:gap-3 transition-all"
            >
              Visit guardiangrace.org
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="lg:col-span-7 text-lg text-teh-muted leading-relaxed space-y-5">
            <p>
              Guardian Grace exists because clinical excellence alone isn't enough. When
              a caregiver can't take a single day off, when a senior misses dialysis because
              they couldn't afford the ride, when a workforce can't enter healthcare because
              tuition is out of reach — that's where the Foundation steps in.
            </p>
            <p>
              As a community partner, Guardian Grace works alongside 1st Touch Enterprise
              Holdings — funded by partnership grants from regional foundations, faith
              communities, and corporate donors who share its mission.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-teh-surfaceAlt">
        <div className="teh-container">
          <p className="teh-overline">Programs</p>
          <h2 className="font-serif text-3xl md:text-5xl mt-3 text-teh-deep mb-14">
            Where the work goes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PROGRAMS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-white p-9 rounded-2xl border border-teh-navy/10">
                  <Icon className="w-10 h-10 text-teh-green mb-6" strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl text-teh-deep">{p.title}</h3>
                  <p className="mt-3 text-teh-muted leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
